import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { Roles } from '@app/core/guard/Roles';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { TableAction, TableActions } from '@app/shared/components/table/table.component';
import { FinancialInstitution } from '@interfaces/a-organizar/financialInstitution';
import { Pagination } from '@interfaces/common/pagination';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogService } from '@services/helpers/dialog.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';

@Component({
  selector: 'app-financial-institution-list-content',
  standalone: true,
  imports: [CommonModule, RouterModule, SharedModule],
  templateUrl: './financial-institution-list-content.component.html',
  styleUrls: ['./financial-institution-list-content.component.scss'],
})
export class FinancialInstitutionListContentComponent extends VaultBaseComponent implements OnInit {
  @Input() groupList: FinancialInstitution[] = [];
  @Input() pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 0,
  };
  @Output() sendTablePagination: EventEmitter<PaginatorState> = new EventEmitter<PaginatorState>();
  @Output() sendTableAction: EventEmitter<TableActions<FinancialInstitution>> = new EventEmitter<
    TableActions<FinancialInstitution>
  >();

  financialInstitutionPermissions = Permissions.FinancialInstitution;
  tableOptions: MenuItem[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  isAdmin = false;
  override Roles = Roles;

  constructor(
    translateService: TranslateService,
    private router: Router,
    private dialogService: ConfirmDialogService,
    private permissionsService: PermissionsService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.tableOptions = [
      {
        label: this.t(this.tk.General.Update),
        action: 'edit',
      },
      {
        label: this.t(this.tk.General.Delete),
        action: 'delete',
      },
    ];

    this.columnDefinitions = [
      new ColumnDefinition({ field: 'logo', header: 'Logo' }),
      new ColumnDefinition({ field: 'sequentialId', header: 'Id' }),
      new ColumnDefinition({ field: 'institutionName', header: 'Nome' }),
      new ColumnDefinition({ field: 'compensationCode', header: 'Código de Compensação' }),
      new ColumnDefinition({ field: 'ispb', header: 'ISPB' }),
      new ColumnDefinition({ field: 'shortName', header: 'Nome Curto' }),
      new ColumnDefinition({ field: 'displayName', header: 'Nome de Exibição' }),
      new ColumnDefinition({ field: 'network', header: 'Rede' }),
      new ColumnDefinition({ field: 'url', header: 'URL' }),
      new ColumnDefinition({ field: 'cnpj', header: 'CNPJ' }),
      new ColumnDefinition({ field: 'options', header: '' }),
    ];

    // Verificar se o usuário é ADMIN
    this.permissionsService.hasRole(Roles.ADMIN).subscribe(isAdmin => {
      this.isAdmin = isAdmin;
    });
  }

  handleTableAction(tableAction: TableAction<FinancialInstitution>) {
    if (tableAction.action === 'edit') {
      this.router.navigate(['dashboard/financial-institution/register', tableAction.item.id]);
    }

    if (tableAction.action === 'delete') {
      this.handleDeleteAction(tableAction.item);
    }

    if (tableAction.action === 'manage-origins') {
      this.router.navigate([
        'dashboard/financial-institution',
        tableAction.item.id,
        'loan-operation-origins',
      ]);
    }
  }

  getMenuActions(item: FinancialInstitution): MenuItem[] {
    const actions: MenuItem[] = [
      {
        label: this.t(this.tk.General.Update),
        icon: 'pi pi-pencil',
        action: 'edit',
        command: () => {
          this.router.navigate(['dashboard/financial-institution/register', item.id]);
        },
      },
      {
        label: this.t(this.tk.General.Delete),
        icon: 'pi pi-trash',
        action: 'delete',
        command: () => {
          this.handleDeleteAction(item);
        },
      },
    ];

    // Adiciona a ação de gerenciar origens apenas para ADMIN
    if (this.isAdmin) {
      actions.push({
        label: 'Gerenciar Origens de Funding',
        icon: 'pi pi-globe',
        action: 'manage-origins',
        command: () => {
          this.router.navigate([
            'dashboard/financial-institution',
            item.id,
            'loan-operation-origins',
          ]);
        },
      });
    }

    return actions;
  }

  addFinancialInstitution(): void {
    this.router.navigate(['dashboard/financial-institution/register']);
  }

  handleTablePagination(pagination: PaginatorState) {
    this.sendTablePagination.emit(pagination);
  }

  handleDeleteAction(item: FinancialInstitution) {
    this.dialogService
      .confirm(
        this.t(this.tk.Management.FinancialInstitutionDescriptionDeleteConfirmation),
        this.t(this.tk.Management.FinancialInstitutionTitleDeleteConfirmation),
        this.t(this.tk.General.Yes),
        this.t(this.tk.General.No)
      )
      .then(response => {
        if (response) {
          this.sendTableAction.emit({ action: 'delete', items: [item] });
        }
      })
      .catch(error => {
        console.error('Error: ', error);
      });
  }

  get formatedGroupList(): FinancialInstitution[] {
    const formated = this.groupList.map((group: FinancialInstitution) => {
      return group;
    });
    return formated;
  }
}
